import { SayferWeb } from './sayfer_web';

class HybridCrypto {
	// Convert string to Uint8Array
	private static strToUint8Array(str: string): Uint8Array {
		return new TextEncoder().encode(str);
	}

	// Convert Uint8Array to Base64 string
	private static uint8ArrayToBase64(arr: Uint8Array): string {
		return btoa(String.fromCharCode(...arr));
	}

	// Convert Base64 string to Uint8Array
	// private static base64ToUint8Array(base64: string): Uint8Array {
	// 	const binaryString = atob(base64);
	// 	return new Uint8Array(binaryString.split('').map(char => char.charCodeAt(0)));
	// }

	private static base64ToUint8Array(base64: string): Uint8Array {
		try {
			// Удаляем пробелы и переносы строк
			const cleanedBase64 = base64.replace(/\n/g, '').replace(/\r/g, '').replace(/\s/g, '');
			const binaryString = atob(cleanedBase64);
			return new Uint8Array(binaryString.split('').map(char => char.charCodeAt(0)));
		} catch (error) {
			throw new Error(`Ошибка декодирования Base64: ${error.message}`);
		}
	}

	// Convert PEM to binary DER format
	// private static pemToBinaryDer(pem: string): Uint8Array {
	// 	console.log('pemToBinaryDer', pem);
	// 	const pemHeader = "-----BEGIN PUBLIC KEY-----";
	// 	const pemFooter = "-----END PUBLIC KEY-----";
	// 	const pemContents = pem.replace(/\n/g, '').substring(pemHeader.length, pem.length - pemFooter.length);
	// 	const binaryDerString = atob(pemContents);
	// 	return new Uint8Array(binaryDerString.split('').map(char => char.charCodeAt(0)));
	// }

	private static pemToBinaryDer(pem: string): Uint8Array {
		try {
			// Определяем тип ключа: публичный или приватный
			const isPub = pem.includes("-----BEGIN PUBLIC KEY-----");
			const isPriv = pem.includes("-----BEGIN PRIVATE KEY-----");
			
			let pemContents;
			if (isPub) {
				// Обработка публичного ключа
				pemContents = pem
					.replace("-----BEGIN PUBLIC KEY-----", '')
					.replace("-----END PUBLIC KEY-----", '')
					.replace(/\n/g, '')
					.trim();
			} else if (isPriv) {
				// Обработка приватного ключа
				pemContents = pem
					.replace("-----BEGIN PRIVATE KEY-----", '')
					.replace("-----END PRIVATE KEY-----", '')
					.replace(/\n/g, '')
					.trim();
			} else {
				throw new Error('Неподдерживаемый формат ключа, ожидается PUBLIC или PRIVATE KEY');
			}
			
			const binaryDerString = atob(pemContents);
			return new Uint8Array(binaryDerString.split('').map(char => char.charCodeAt(0)));
		} catch (error) {
			throw new Error(`Ошибка декодирования PEM: ${error.message}`);
		}
	}

	// Convert binary DER to PEM format
	private static binaryDerToPem(der: ArrayBuffer, type: 'PUBLIC' | 'PRIVATE'): string {
		const base64 = this.uint8ArrayToBase64(new Uint8Array(der));
		const header = `-----BEGIN ${type} KEY-----\n`;
		const footer = `\n-----END ${type} KEY-----`;
		const wrapped = base64.match(/.{1,64}/g)?.join('\n') || base64;
		return header + wrapped + footer;
	}

	// Generate RSA key pair
	public static async generateKeyPair(): Promise<{
		publicKeyPem: string;
		privateKeyPem: string;
	}> {
		const keyPair = await crypto.subtle.generateKey(
			{
				name: 'RSA-OAEP',
				modulusLength: 2048,
				publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
				hash: 'SHA-256',
			},
			true,
			['encrypt', 'decrypt']
		);

		const publicKeyDer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
		const privateKeyDer = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

		const publicKeyPem = this.binaryDerToPem(publicKeyDer, 'PUBLIC');
		const privateKeyPem = this.binaryDerToPem(privateKeyDer, 'PRIVATE');

		return { publicKeyPem, privateKeyPem };
	}

	// Import public key from PEM
	private static async importPublicKey(pem: string): Promise<CryptoKey> {
		const binaryDer = this.pemToBinaryDer(pem);
		return await crypto.subtle.importKey(
			'spki',
			binaryDer,
			{
				name: 'RSA-OAEP',
				hash: 'SHA-256',
			},
			true,
			['encrypt']
		);
	}

	// Import private key from PEM
	// private static async importPrivateKey(pem: string): Promise<CryptoKey> {
	// 	const pemCleaned = pem.replace('-----BEGIN PRIVATE KEY-----', '-----BEGIN PUBLIC KEY-----')
	// 	                     .replace('-----END PRIVATE KEY-----', '-----END PUBLIC KEY-----');
	// 	const binaryDer = this.pemToBinaryDer(pemCleaned);
	// 	return await crypto.subtle.importKey(
	// 		'pkcs8',
	// 		binaryDer,
	// 		{
	// 			name: 'RSA-OAEP',
	// 			hash: 'SHA-256',
	// 		},
	// 		true,
	// 		['decrypt']
	// 	);
	// }

	private static async importPrivateKey(pem: string): Promise<CryptoKey> {
		try {
			const binaryDer = this.pemToBinaryDer(pem); // Передаем PEM без изменений
			return await crypto.subtle.importKey(
				'pkcs8',
				binaryDer,
				{
					name: 'RSA-OAEP',
					hash: 'SHA-256',
				},
				true,
				['decrypt']
			);
		} catch (error) {
			throw new Error(`Ошибка импорта приватного ключа: ${error.message}`);
		}
	}

	// Encrypt a short message (e.g., password) with RSA
	public static async encrypt(publicKeyPem: string, message: string): Promise<string> {
		if (new TextEncoder().encode(message).length > 214) {
			throw new Error('Сообщение слишком длинное для RSA-OAEP');
		}
		const publicKey = await this.importPublicKey(publicKeyPem);
		const encodedMessage = this.strToUint8Array(message);
		const encryptedMessage = await crypto.subtle.encrypt(
			{
				name: 'RSA-OAEP',
			},
			publicKey,
			encodedMessage
		);
		return this.uint8ArrayToBase64(new Uint8Array(encryptedMessage));
	}

	// Decrypt a short message (e.g., password) with RSA
	public static async decrypt(privateKeyPem: string, encryptedBase64: string): Promise<string> {
		const privateKey = await this.importPrivateKey(privateKeyPem);
		const encryptedData = this.base64ToUint8Array(encryptedBase64);
		const decryptedMessage = await crypto.subtle.decrypt(
			{
				name: 'RSA-OAEP',
			},
			privateKey,
			encryptedData
		);
		return new TextDecoder().decode(decryptedMessage);
	}

	// Decrypt not base64 encoded data
	// public static async decryptRaw(privateKeyPem: string, encryptedData: Uint8Array): Promise<string> {
	// 	const privateKey = await this.importPrivateKey(privateKeyPem);
	// 	const decryptedMessage = await crypto.subtle.decrypt(
	// 		{
	// 			name: 'RSA-OAEP',
	// 		},
	// 		privateKey,
	// 		encryptedData
	// 	);
	// 	return new TextDecoder().decode(decryptedMessage);
	// }

	public static async decryptRaw(privateKeyPem: string, encryptedData: string): Promise<string> {
		try {
			// Декодируем Base64 в Uint8Array
			const encodedData = this.base64ToUint8Array(encryptedData);
	
			// Проверяем длину данных (должно быть 256 байт для RSA-OAEP с 2048-битным ключом)
			if (encodedData.length !== 256) {
				throw new Error(`Некорректная длина зашифрованных данных: ${encodedData.length} байт, ожидалось 256`);
			}
	
			// Импортируем приватный ключ
			const privateKey = await this.importPrivateKey(privateKeyPem);
	
			// Расшифровываем данные
			const decryptedMessage = await crypto.subtle.decrypt(
				{
					name: 'RSA-OAEP',
				},
				privateKey,
				encodedData
			);
	
			// Декодируем результат в строку UTF-8
			return new TextDecoder().decode(decryptedMessage);
		} catch (error) {
			console.error('Ошибка в decryptRaw:', error);
			throw new Error(`Ошибка при расшифровке: ${error.message}`);
		}
	}

	// Hybrid encryption: AES-GCM for data, RSA for password
	public static async hybridEncrypt(
		publicKeyPem: string,
		data: string | object,
		password: string = crypto.randomUUID()
	): Promise<{
		encryptedData: string;
		encryptedPassword: string;
	}> {
		const sayfer = new SayferWeb(password);
		const dataToEncrypt = typeof data === 'string' ? data : JSON.stringify(data);
		const { key: encryptedData } = await sayfer.encrypt(dataToEncrypt);
		const encryptedPassword = await this.encrypt(publicKeyPem, password);
		return { encryptedData, encryptedPassword };
	}

	// Hybrid decryption: RSA for password, AES-GCM for data
	public static async hybridDecrypt(
		privateKeyPem: string,
		encryptedData: string,
		encryptedPassword: string
	): Promise<string> {
		const password = await this.decrypt(privateKeyPem, encryptedPassword);
		const sayfer = new SayferWeb(password);
		return await sayfer.decrypt(encryptedData);
	}

	// Hybrid encryption for records (objects)
	public static async hybridEncryptRecords(
		publicKeyPem: string,
		records: any[],
		password: string = crypto.randomUUID()
	): Promise<{
		encryptedData: string;
		encryptedPassword: string;
	}> {
		const sayfer = new SayferWeb(password);
		const { key: encryptedData } = await sayfer.encryptRecords(records);
		const encryptedPassword = await this.encrypt(publicKeyPem, password);
		return { encryptedData, encryptedPassword };
	}

	// Hybrid decryption for records (objects)
	public static async hybridDecryptRecords(
		privateKeyPem: string,
		encryptedData: string,
		encryptedPassword: string
	): Promise<any[]> {
		const password = await this.decrypt(privateKeyPem, encryptedPassword);
		const sayfer = new SayferWeb(password);
		return await sayfer.decryptRecords(encryptedData);
	}
}

export default HybridCrypto;